import React, { useState } from 'react';
import { Patient } from '../../store/types/patient';
import { AbhaIconNotLinked, loaderIcon } from '../common/Icons';
import CurrMonthLossStrip from '../common/CurrMonthLossStrip';
import { DAYS, MONTHS, YEARS } from '../common/constants/add-patient';
import { DFormEntity } from '../../api/fetchDoctorGenieByOID';
import PatientNameAutoSuggestBlock from '../PatientNameAutoSuggestBlock';
import AgeInputBlock from '../AgeInputBlock';
import moment from 'moment';
import { IHealthId } from '../../api/abha/confirm-otp';
import { Gender } from '../../pages/NewPatient';
import GenderBlock from '../GenderBlock';
import { loginAbhaProfile } from '../../api/abha/login';
import { createAbha } from '../../api/abha/create-abha';
import { getAbhaSuggestions, splitFullName } from '../../store/utils';
import { getDoc, getMe, getMySingleDoctorId, getSelectedClinicId } from '../../store/idbHandler';
import { track } from '../../tracking';
import { captureError } from '../../pages/captureError';
import { notification } from 'antd';
import PhoneNoOtpComponent from './PhoneNoOtpComponent';
import { regex } from './constants';
import { useParams } from 'react-router-dom';

type IFormState = 'abha' | 'add-pt';

interface Props {
    phCode: string;
    phoneNumber: string | undefined;
    currMonthLoss: number | undefined;
    dfValues: {
        [k: string]: string | number;
    };
    name: string | undefined;
    isNameError: boolean;
    isAgeDOBError: boolean;
    isPhoneNumberError: boolean;
    isDobError: boolean;
    currMomentDate?: moment.Moment | null;
    onAgeSelect: (
        option: {
            id: YEARS | MONTHS | DAYS;
            label: string;
            value: number;
        } | null,
    ) => void;
    onDobChange: any;
    value: {
        id: YEARS | MONTHS | DAYS;
        label: string;
        value: number;
    } | null;
    onAutoComplete: (patient?: Patient) => void;
    onNameChange: (input?: string) => void;
    onPhoneNumberChange: (input?: string) => void;
    salutation: DFormEntity | undefined;
    salutationChangeHandler: (e?: React.ChangeEvent<HTMLSelectElement>) => void;
    disabledDate?: (date: moment.Moment) => boolean;
    setAddPtFormState?: (formstate: IFormState | undefined) => void;
    isGenderInputDisabled: boolean;
    isGenderError: boolean;
    onGenderChange: (gender?: Gender) => void;
    gender: Gender | undefined;
    setAbhaFields: (opt: { skipState: string; abhaId: string | undefined }) => void;
    dob:
        | {
              dd?: string | undefined;
              mm?: string | undefined;
              yyyy?: string | undefined;
          }
        | undefined;
    ageDays: string | undefined;
    ageMonth: string | undefined;
    isModal: boolean;
    onDDDobChange?: (e: React.ChangeEvent<HTMLSelectElement> | string) => void;
    onMMDobChange?: (e: React.ChangeEvent<HTMLSelectElement> | string) => void;
    onYYYYDobChange?: (e: React.ChangeEvent<HTMLSelectElement> | string) => void;
    today?: moment.Moment;
    patientId: string | undefined;
    authHealthId: string | undefined;
    showSendOtpScreen: boolean;
    setShowSendOtpScreen: (val: boolean) => void;
    abhaId: string | undefined;
    setIsPhoneNumberError: (val: boolean) => void;
    errorValidation: () => {
        success: boolean;
        dobStr?: string;
    };
    resetError: () => void;
    setAuthHealthId: (val: string | undefined) => void;
    abhaLinkPatient?: Patient;
    closeModal?: () => void;
    onAbhaLink?: (pt: Patient) => void;
    setShowAbhaCard: (val: boolean) => void;
    fromNdhmDiscovery?: boolean;
    defaultAbhaId?: string;
    cId?: string;
    setAbhaPtOid: (oid?: string) => void;
}

const CreateAbhaForm: React.FC<Props> = ({
    isPhoneNumberError,
    phCode,
    phoneNumber,
    currMonthLoss,
    dfValues,
    name,
    isNameError,
    isAgeDOBError,
    isDobError,
    currMomentDate,
    onAgeSelect,
    onDobChange,
    value,
    onAutoComplete,
    onPhoneNumberChange,
    onNameChange,
    salutation,
    salutationChangeHandler,
    disabledDate,
    setAddPtFormState,
    gender,
    isGenderError,
    isGenderInputDisabled,
    onGenderChange,
    setAbhaFields,
    ageDays,
    ageMonth,
    dob,
    isModal,
    onDDDobChange,
    onMMDobChange,
    onYYYYDobChange,
    today,
    patientId,
    authHealthId,
    showSendOtpScreen,
    setShowSendOtpScreen,
    abhaId,
    setIsPhoneNumberError,
    errorValidation,
    resetError,
    setAuthHealthId,
    abhaLinkPatient,
    closeModal,
    onAbhaLink,
    setShowAbhaCard,
    fromNdhmDiscovery,
    defaultAbhaId,
    cId,
    setAbhaPtOid,
}) => {
    const [txnId, setTxnId] = useState<string>();
    const [otp, setOtp] = useState('');

    const [generateOtpState, setGenerateOtpState] = useState<
        'none' | 'loading' | 'success' | 'error'
    >('none');

    const [confirmState, setConfirmState] = useState<
        'none' | 'loading' | 'error' | 'options' | 'no-options' | 'linking'
    >('none');

    const [healthIds, setHealthIds] = useState<IHealthId[] | null>(null);
    const [isCreateLoading, setIsCreateLoading] = useState(false);

    const [selectedHealthId, setSelectedHealthId] = useState<IHealthId | null>(null);

    const setOtpHandler = (otpValue: string) => setOtp(otpValue);

    const [isOtpDisabled, setIsOtpDisabled] = useState(true);
    const [errorString, setErrorString] = useState<string>();
    const [isNameDisabled, setIsNameDisabled] = useState(false);

    const setGenerateOtpStateHandler = (otpValue: 'none' | 'loading' | 'success' | 'error') =>
        setGenerateOtpState(otpValue);

    const setTxnIdHandler = (txnId?: string) => setTxnId(txnId);
    const setHealthIdHandler = (healthIds: IHealthId[] | null) => setHealthIds(healthIds);
    const setConfirmStateHandler = (
        val: 'none' | 'loading' | 'error' | 'options' | 'no-options' | 'linking',
    ) => {
        if (defaultAbhaId) {
            setConfirmState('options');
            setSelectedHealthId({ health_id: defaultAbhaId });

            onCreateAbha('options', defaultAbhaId);
        } else {
            setConfirmState(val);
        }
    };
    const selectedHealthIdHandler = (val: IHealthId | null) => setSelectedHealthId(val);
    const setIsOtpDisabledHandler = (val: boolean) => setIsOtpDisabled(val);
    const setErrorStringHandler = (val: string | undefined) => setErrorString(val);

    const enableCreateAbha =
        confirmState === 'options'
            ? true
            : name &&
              gender &&
              phoneNumber &&
              (value ||
                  currMomentDate ||
                  (dob && dob.dd !== '0' && dob.mm !== '0' && dob.yyyy !== '0'));

    const createFlow = (skipState?: string) => {
        if (skipState && skipState === 'abha_end') {
            setAbhaFields({ skipState: 'abha_end', abhaId: authHealthId });
            setAddPtFormState && setAddPtFormState('add-pt');
            return;
        }

        const check =
            name &&
            phoneNumber &&
            txnId &&
            (value ||
                currMomentDate ||
                (dob && dob.dd !== '0' && dob.mm !== '0' && dob.yyyy !== '0'));

        if (check) {
            const invalid = regex.test(name as string);

            if (invalid) {
                notification.error({
                    message: "Name can't have special characters or digits",
                    duration: 5,
                });
                return;
            }

            const pin =
                getDoc()?.profile.professional.clinics && getDoc()?.profile.professional.clinics![0]
                    ? getDoc()?.profile.professional.clinics![0]?.address?.pin
                    : undefined;

            track('ABHA_CREATE_TRIGGERED');

            let date: moment.Moment;

            if (dob && dob.dd !== '0' && dob.mm !== '0' && dob.yyyy !== '0') {
                date = moment(`${dob.dd}-${dob.mm}-${dob.yyyy}`, 'DD-MM-YYYY');
            } else if (ageDays) {
                date = moment().subtract(parseInt(ageDays), 'days');
            } else if (ageMonth) {
                date = moment().subtract(parseInt(ageMonth), 'months');
            } else {
                // THIS should not go here, as age is mandate for ABHA doctors
                track('ERROR', {
                    on: 'CreateABHAForm',
                    what: 'default age',
                    age: JSON.stringify({ dob, ageDays, ageMonth }),
                });
                date = moment('01-01-1992', 'DD-MM-YYYY');
            }

            const nameObj = splitFullName(name!);

            setIsCreateLoading(true);

            const suggestedAbhaId = getAbhaSuggestions(name!, phoneNumber!);

            createAbha(
                getMySingleDoctorId() || '',
                cId || params.cId || getSelectedClinicId() || '',
                getMe()?.business?.id || '',
                {
                    txn_id: txnId!,
                    day_of_birth: date.date().toString(),
                    month_of_birth: (date.month() + 1).toString(),
                    year_of_birth: date.year().toString(),
                    first_name: nameObj.firstName,
                    gender: gender === 'Male' ? 'M' : gender === 'Female' ? 'F' : 'O',
                    health_id: suggestedAbhaId,
                    last_name: nameObj.lastName,
                    middle_name: nameObj.middleName,
                    name: name!,
                    pincode: pin ?? '560048',
                },
            )
                .then((res) => {
                    if (res.error) {
                        throw new Error(res.error);
                    }
                    track('ABHA_CREATE_SUCCESS');
                    setIsCreateLoading(false);
                    setAddPtFormState && setAddPtFormState('add-pt');
                    setAbhaFields(
                        res.skip_state === 'abha_queued'
                            ? { skipState: res.skip_state, abhaId: suggestedAbhaId }
                            : { skipState: res.skip_state, abhaId: res.health_id },
                    );
                    setAbhaPtOid(res.oid);
                })
                .catch((e) => {
                    notification.error({
                        message: e?.message ?? 'Something wrong happened!',
                        duration: 5,
                    });
                    setIsCreateLoading(false);
                    track('ABHA_CREATE_FAILED', {
                        txnId,
                        error: e?.message,
                        txn_id: txnId,
                        day_of_birth: date.day().toString(),
                        month_of_birth: date.month().toString(),
                        year_of_birth: date.year().toString(),
                        first_name: nameObj.firstName,
                        gender: gender === 'Male' ? 'M' : gender === 'Female' ? 'F' : 'O',
                        health_id: suggestedAbhaId,
                        last_name: nameObj.lastName,
                        middle_name: nameObj.middleName,
                        name: name,
                        pincode: pin ?? '560048',
                    });
                    // captureError(new Error(`ABHA: ${e?.message}`));
                });
        } else {
            setIsNameDisabled(true);
            setConfirmState('no-options');
        }
    };

    const params = useParams<{ cId: string }>();

    const onCreateAbha = (cs?: string, health_id?: string) => {
        if (txnId) {
            if ((cs || confirmState) === 'options') {
                track('ABHA_LOGIN_TRIGGERED', {
                    abhaLinkPatient: abhaLinkPatient?.id ?? '',
                });

                setIsCreateLoading(true);

                loginAbhaProfile(
                    getMySingleDoctorId() || '',
                    cId || params.cId || getSelectedClinicId() || '',
                    {
                        health_id: (health_id || selectedHealthId?.health_id) ?? '',
                        txn_id: txnId,
                    },
                )
                    .then((res) => {
                        if (res.error) {
                            throw new Error(res.error);
                        }

                        if (abhaLinkPatient) {
                            notification.success({
                                message: 'ABHA linked successfully!',
                                duration: 5,
                            });
                            track('ABHA_LOGIN_SUCCESS', {
                                abhaLinkPatient: abhaLinkPatient?.id ?? '',
                            });
                            closeModal && closeModal();
                            onAbhaLink && onAbhaLink(abhaLinkPatient);
                            return;
                        }

                        track('ABHA_LOGIN_SUCCESS');
                        setIsCreateLoading(false);
                        setAddPtFormState && setAddPtFormState('add-pt');
                        setAbhaPtOid(res.oid);
                        setAbhaFields({ skipState: 'abha_end', abhaId: res.health_id });
                        onPhoneNumberChange(phoneNumber ?? '');
                        onNameChange(`${res.first_name} ${res.middle_name} ${res.last_name}`);
                        onGenderChange(
                            res.gender === 'M' ? 'Male' : res.gender === 'F' ? 'Female' : 'Other',
                        );
                        if (res.month_of_birth && res.day_of_birth && res.year_of_birth) {
                            if (isModal) {
                                const date = moment(
                                    `${res.month_of_birth}/${res.day_of_birth}/${res.year_of_birth}`,
                                    'MM/DD/YYYY',
                                );
                                onDobChange(date);
                            } else {
                                onDDDobChange && onDDDobChange(res?.day_of_birth ?? '');
                                onMMDobChange && onMMDobChange(res?.month_of_birth ?? '');
                                onYYYYDobChange && onYYYYDobChange(res?.year_of_birth ?? '');
                            }
                        } else {
                            if (res.year_of_birth) {
                                onAgeSelect({
                                    id: 'MONTHS',
                                    value: moment().diff(res.year_of_birth, 'months'),
                                    label: `${moment().diff(res.year_of_birth, 'months')} Months`,
                                });
                            }
                        }
                    })
                    .catch((e) => {
                        notification.error({
                            message: e?.message ?? 'Something went wrong!',
                            duration: 5,
                        });

                        if (abhaLinkPatient) {
                            closeModal && closeModal();
                            track('ABHA_LOGIN_FAILED', {
                                txnId,
                                health_id: selectedHealthId?.health_id ?? '',
                                error: e.message,
                                abhaLinkPatient: abhaLinkPatient.id,
                            });
                        } else {
                            setIsCreateLoading(false);
                            track('ABHA_LOGIN_FAILED', {
                                txnId,
                                health_id: selectedHealthId?.health_id ?? '',
                                error: e.message,
                            });
                            // captureError(new Error(`ABHA: ${e?.message}`));
                        }
                    });
            }

            if (confirmState === 'no-options') {
                createFlow();
            }
        }
    };

    const validateAndCreateAbha = () => {
        const data = errorValidation();

        if (data.success || confirmState === 'options') {
            onCreateAbha();
        }
    };

    const skipAbha = () => {
        track('SKIP_ABHA', { error: errorString ?? 'No error' });
        onPhoneNumberChange(undefined);
        onNameChange(undefined);
        onAgeSelect(null);
        onDobChange(null);
        onGenderChange(undefined);
        salutationChangeHandler();
        setShowAbhaCard(false);
        setAddPtFormState && setAddPtFormState('add-pt');
    };

    return (
        <div className="flex flex-col h-full w-full gap-8">
            <div className="w-full">
                <div className="m-12 flex items-center gap-8">
                    <span>{AbhaIconNotLinked}</span>
                    <p className="text-20 font-400">
                        {abhaLinkPatient ? 'Link' : 'Add'} patient ABHA
                    </p>
                </div>
                <CurrMonthLossStrip currMonthLoss={currMonthLoss} className="mt-4 gap-8" />
            </div>
            <div className="flex flex-col w-full h-full overflow-scroll hide-scrollbar p-16 gap-16">
                <PhoneNoOtpComponent
                    phCode={phCode}
                    gender={gender}
                    confirmState={confirmState}
                    errorString={errorString}
                    generateOtpState={generateOtpState}
                    healthIds={healthIds}
                    isOtpDisabled={isOtpDisabled}
                    isPhoneNumberError={isPhoneNumberError}
                    name={name}
                    onAutoComplete={onAutoComplete}
                    onPhoneNumberChange={onPhoneNumberChange}
                    otp={otp}
                    phoneNumber={phoneNumber}
                    selectedHealthId={selectedHealthId}
                    selectedHealthIdHandler={selectedHealthIdHandler}
                    setConfirmStateHandler={setConfirmStateHandler}
                    setErrorStringHandler={setErrorStringHandler}
                    setGenerateOtpStateHandler={setGenerateOtpStateHandler}
                    setHealthIdHandler={setHealthIdHandler}
                    setIsOtpDisabledHandler={setIsOtpDisabledHandler}
                    setOtpHandler={setOtpHandler}
                    setTxnIdHandler={setTxnIdHandler}
                    txnId={txnId}
                    isModal={isModal}
                    patientId={patientId}
                    authHealthId={authHealthId}
                    showSendOtpScreen={showSendOtpScreen}
                    setShowSendOtpScreen={setShowSendOtpScreen}
                    setAbhaFields={setAbhaFields}
                    setAddPtFormState={setAddPtFormState}
                    abhaId={abhaId}
                    setIsNameDisabled={(val: boolean) => setIsNameDisabled(val)}
                    setIsPhoneNumberError={setIsPhoneNumberError}
                    createFlow={createFlow}
                    resetError={resetError}
                    setAuthHealthId={setAuthHealthId}
                    abhaLinkPatient={abhaLinkPatient}
                    closeModal={closeModal}
                    onAbhaLink={onAbhaLink}
                    fromNdhmDiscovery={fromNdhmDiscovery}
                    cId={cId}
                />
                {confirmState === 'no-options' ? (
                    <div
                        className={`flex w-full items-center ${
                            isModal ? 'gap-16' : 'gap-24 mt-8'
                        } flex-col`}
                    >
                        <div className="flex flex-col w-full gap-12 items-start">
                            {isModal && <p className="text-13 font-700">Patient Name *</p>}
                            <PatientNameAutoSuggestBlock
                                dfValues={dfValues}
                                disabled={isNameDisabled}
                                onAutoComplete={onAutoComplete}
                                onNameChange={onNameChange}
                                salutation={salutation}
                                salutationChangeHandler={salutationChangeHandler}
                                suggestionsEnabled={!phoneNumber}
                                name={name}
                                isNameError={isNameError}
                                isNameInputDisabled={false}
                                isModal={isModal}
                            />
                        </div>
                        <div
                            className={`flex ${
                                isModal ? 'flex-col' : ''
                            } w-full gap-12 items-start`}
                        >
                            {isModal && <p className="text-13 font-700">Age *</p>}
                            <AgeInputBlock
                                disabledDate={disabledDate}
                                isAgeInputDisabled={false}
                                isAgeDOBError={isAgeDOBError}
                                isDobError={isDobError}
                                momentDate={currMomentDate}
                                onAgeSelect={onAgeSelect}
                                onDobChange={onDobChange}
                                value={value}
                                isModal={isModal}
                                dob={dob}
                                onDDDobChange={onDDDobChange}
                                onMMDobChange={onMMDobChange}
                                onYYYYDobChange={onYYYYDobChange}
                                today={today}
                            />
                        </div>
                        <div className="flex flex-col w-full gap-12 items-start">
                            {isModal && <p className="text-13 font-700">Gender *</p>}
                            <GenderBlock
                                gender={gender}
                                isGenderError={isGenderError}
                                isGenderInputDisabled={isGenderInputDisabled}
                                onGenderChange={onGenderChange}
                                isModal={isModal}
                            />
                        </div>
                    </div>
                ) : null}
            </div>
            <div className="flex border-t p-16 border-darwin-neutral_new-200 justify-between mt-auto align-center">
                {!abhaLinkPatient && (
                    <button
                        className={`border cursor-pointer font-700 ${
                            isCreateLoading
                                ? 'border-darwin-neutral_new-200 text-darwin-neutral_new-200'
                                : 'border-darwin-accent-bright-blue-800 text-darwin-accent-bright-blue-800'
                        } justify-center py-6 px-8`}
                        onClick={skipAbha}
                        style={{
                            borderRadius: '0.5rem',
                        }}
                        disabled={isCreateLoading}
                    >
                        Skip ABHA
                    </button>
                )}
                <button
                    className={`font-700 flex items-center gap-4 ${
                        !enableCreateAbha ||
                        isCreateLoading ||
                        confirmState === 'error' ||
                        confirmState === 'loading' ||
                        confirmState === 'linking' ||
                        confirmState === 'none'
                            ? 'bg-darwin-neutral_new-200'
                            : 'bg-darwin-accent-bright-blue-800'
                    } text-white justify-center p-8 ${abhaLinkPatient ? 'ml-auto' : ''}`}
                    onClick={validateAndCreateAbha}
                    style={{
                        borderRadius: '0.5rem',
                    }}
                    disabled={
                        confirmState === 'error' ||
                        confirmState === 'loading' ||
                        confirmState === 'linking' ||
                        confirmState === 'none' ||
                        !enableCreateAbha ||
                        isCreateLoading
                    }
                >
                    {isCreateLoading && loaderIcon('w-16 h-16 fill-white font-700 animate-spin')}
                    {abhaLinkPatient ? 'Link ABHA' : 'Create & continue'}
                </button>
            </div>
        </div>
    );
};

export default CreateAbhaForm;
